import React, { useCallback } from 'react';
import Icon from '../../../base/icons/components/Icon';
import { IconCheck } from '../../../base/icons/svg';
import { reasonKey } from '../../copy';
import { dismiss, invite } from '../../store';
import SiFace from './SiFace';
/**
 * The words and tone of an SI's availability.
 *
 * @param {IInvitableSi} si - The SI.
 * @param {Words} w - Words.
 * @returns {Object}
 */
function statusOf(si, w) {
    if (si.status === 'PAUSED' && (si.pausedBy === 'ORG' || si.pausedBy === 'PLATFORM')) {
        return { text: w('siInvite.status.PAUSED_BY_ORG'),
            tone: 'muted' };
    }
    const tone = {
        AVAILABLE: 'ok',
        OFFLINE: 'idle',
        ON_A_CALL: 'busy',
        PAUSED: 'muted',
        UNAVAILABLE: 'muted',
        NOT_SET_UP: 'muted',
        UNKNOWN: 'idle'
    };
    return { text: w(`siInvite.status.${si.status}`),
        tone: tone[si.status] ?? 'idle' };
}
/**
 * One SI: face, name, job title, availability, and its invite button through inviting, joining, joined or failed.
 *
 * @returns {ReactElement}
 */
export default function SiCard({ index, pending, si, w }) {
    const phase = pending?.phase;
    const status = statusOf(si, w);
    const busy = phase === 'inviting' || phase === 'joining';
    const onInvite = useCallback(() => {
        invite(si);
    }, [si]);
    const onDismiss = useCallback(() => dismiss(si.memberId), [si.memberId]);
    const failure = phase === 'failed'
        ? w(reasonKey(pending?.reason), { name: si.name.split(' ')[0] || si.name })
        : null;
    const locked = si.group === 'LOCKED' && !phase;
    const firstName = si.name.split(' ')[0] || si.name;
    const subline = phase === 'joining' || phase === 'inviting' ? w('siInvite.connecting')
        : phase === 'joined' ? w('siInvite.inMeeting') : status.text;
    return (React.createElement("li", { className: `ksi-card ksi-phase-${phase ?? 'idle'}${locked ? ' ksi-locked' : ''}`, style: { animationDelay: `${Math.min(index, 8) * 35}ms` } },
        React.createElement("div", { className: 'ksi-row' },
            React.createElement("div", { className: `ksi-face${busy ? ' ksi-face-busy' : ''}` },
                React.createElement(SiFace, { name: si.name, size: 44, url: si.avatarUrl }),
                React.createElement("span", { "aria-hidden": 'true', className: `ksi-dot ksi-tone-${phase === 'joined' ? 'ok' : status.tone}` }),
                phase === 'joined' && (React.createElement("span", { "aria-hidden": 'true', className: 'ksi-check' },
                    React.createElement(Icon, { size: 14, src: IconCheck })))),
            React.createElement("div", { className: 'ksi-who' },
                React.createElement("div", { className: 'ksi-name' }, si.name),
                si.jobTitle && React.createElement("div", { className: 'ksi-title' }, si.jobTitle),
                React.createElement("div", { "aria-live": 'polite', className: `ksi-status ksi-tone-text-${phase === 'joined' ? 'ok' : status.tone}` }, subline)),
            React.createElement("div", { className: 'ksi-action' }, phase === 'joined' ? (React.createElement("span", { className: 'ksi-joined-pill' },
                React.createElement(Icon, { size: 14, src: IconCheck }),
                w('siInvite.joined'))) : locked ? (React.createElement("span", { className: 'ksi-unavailable-pill' }, w('siInvite.askSupervisor'))) : !si.invitable && !phase ? (React.createElement("span", { className: 'ksi-unavailable-pill' }, w('siInvite.notNow'))) : (React.createElement("button", { "aria-label": w('siInvite.inviteName', { name: si.name }), className: `ksi-btn${busy ? ' ksi-btn-busy' : ''}`, disabled: busy || !si.invitable, onClick: onInvite, type: 'button' },
                busy && React.createElement("span", { "aria-hidden": 'true', className: 'ksi-spinner' }),
                React.createElement("span", null, phase === 'inviting' ? w('siInvite.inviting')
                    : phase === 'joining' ? w('siInvite.joining')
                        : phase === 'failed' ? w('siInvite.tryAgain') : w('siInvite.invite')))))),
        locked && (React.createElement("div", { className: 'ksi-locked-why' }, w(`siInvite.locked.${si.lockedReason === 'NOT_ALLOWED_TO_INVITE_THIS_SI'
            ? 'NOT_ALLOWED_TO_INVITE_THIS_SI' : 'SI_NEEDS_SUPERVISOR'}`, { name: firstName }))),
        failure && (React.createElement("div", { className: 'ksi-failure', role: 'alert' },
            React.createElement("span", null, failure),
            React.createElement("button", { className: 'ksi-link', onClick: onDismiss, type: 'button' }, w('siInvite.dismiss'))))));
}
